import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ScorecardAgentService,
  TeeChange,
  ActiveEventWarning,
  UploadAgentResponse,
  SkippedTeeDetail
} from '../../services/scorecard-agent.service';

export interface ScorecardUploadDialogData {
  course_id: string;
}

export type DialogStep = 'pick-file' | 'processing' | 'review' | 'missing-rating' | 'applying' | 'wrong-course' | 'rating';

export interface ProgressItem {
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

@Component({
  selector: 's18-scorecard-preview-dialog',
  templateUrl: './scorecard-preview-dialog.component.html',
  styleUrls: ['./scorecard-preview-dialog.component.scss']
})
export class ScorecardPreviewDialogComponent {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  // Dialog state
  step: DialogStep = 'pick-file';
  errorMessage: string = '';

  // File picker
  selectedFileName: string = '';
  fileError: string = '';

  // Processing progress
  progressSteps: ProgressItem[] = [];
  elapsedSeconds: number = 0;
  private elapsedTimer: any = null;

  // Review data
  warnings: ActiveEventWarning[] = [];
  teeChanges: TeeChange[] = [];
  skippedTees: string[] = [];
  skippedTeesDetails: SkippedTeeDetail[] = [];
  sessionId: string = '';
  holeNumbers: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  // Applying state
  applying: boolean = false;

  // Rating state
  userRating: number | null = null;
  userNotes: string = '';
  private applyResult: boolean = false;

  // Missing rating state
  missingRatingTees: string[] = [];

  // Wrong course state
  detectedCourseName: string = '';
  expectedCourseName: string = '';

  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  private courseId: string;
  private imageBase64: string = '';

  constructor(
    private dialogRef: MatDialogRef<ScorecardPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ScorecardUploadDialogData,
    private scorecardAgentService: ScorecardAgentService,
    private snackbar: MatSnackBar
  ) {
    this.courseId = data.course_id;
  }

  // ─── File Picker Step ─────────────────────────────────────────────────────

  onPickFile(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.fileError = '';

    if (file.size > this.MAX_FILE_SIZE) {
      this.fileError = 'File exceeds the 10 MB size limit.';
      return;
    }

    this.selectedFileName = file.name;
    this.startUpload(file);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    this.fileError = '';
    if (file.size > this.MAX_FILE_SIZE) {
      this.fileError = 'File exceeds the 10 MB size limit.';
      return;
    }

    if (!file.type.match(/^image\/(jpeg|png|avif)$/)) {
      this.fileError = 'Unsupported format. Please use JPG, PNG, or AVIF.';
      return;
    }

    this.selectedFileName = file.name;
    this.startUpload(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  // ─── Processing Step ──────────────────────────────────────────────────────

  private startUpload(file: File): void {
    this.step = 'processing';
    this.errorMessage = '';
    this.progressSteps = [
      { label: 'Preparing image...', status: 'active' },
      { label: 'Analyzing scorecard (this may take a few passes)...', status: 'pending' },
      { label: 'Validating extracted data...', status: 'pending' },
      { label: 'Building preview...', status: 'pending' }
    ];

    // If the file is AVIF, convert to PNG client-side (OpenAI doesn't support AVIF)
    if (file.type === 'image/avif') {
      this.convertToBase64Png(file).then(pngBase64 => {
        this.imageBase64 = pngBase64;
        this.advanceProgress(0);
        this.startElapsedTimer();
        setTimeout(() => {
          this.advanceProgress(1);
          this.callUploadApi();
        }, 300);
      }).catch(() => {
        this.showError('Could not process AVIF image. Please convert to JPG or PNG and try again.');
      });
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        this.imageBase64 = (reader.result as string).split(',')[1];
        this.advanceProgress(0);
        this.startElapsedTimer();
        setTimeout(() => {
          this.advanceProgress(1);
          this.callUploadApi();
        }, 300);
      };
      reader.readAsDataURL(file);
    }
  }

  private startElapsedTimer(): void {
    this.elapsedSeconds = 0;
    this.elapsedTimer = setInterval(() => {
      this.elapsedSeconds++;
    }, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  /**
   * Converts an image file (including AVIF) to PNG base64 using Canvas.
   * The browser handles decoding AVIF natively, then we export as PNG.
   */
  private convertToBase64Png(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Canvas not available'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(dataUrl.split(',')[1]);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Image decode failed'));
      };
      img.src = url;
    });
  }

  private callUploadApi(): void {
    this.scorecardAgentService.uploadScorecard(this.courseId, this.imageBase64)
      .subscribe(
        resp => {
          if (resp.success && resp.session_id) {
            this.sessionId = resp.session_id;
            this.advanceProgress(1);
            this.startPolling();
          } else {
            this.showError(resp.error || 'Upload failed. Please try again.');
          }
        },
        err => {
          this.showError('Could not reach the server. Please try again later.');
        }
      );
  }

  private pollingTimer: any = null;
  statusMessage: string = '';
  private pollCount: number = 0;

  private startPolling(): void {
    this.pollCount = 0;
    this.pollingTimer = setInterval(() => {
      this.pollCount++;
      this.scorecardAgentService.getProcessStatus(this.sessionId)
        .subscribe(
          resp => {
            const status = resp.status || '';
            // When process is done, the DB record is deleted and getProcessStatus returns "Loading ..."
            // Only treat this as "done" after we've polled at least a couple times (to avoid race condition on start)
            const isDone = (status.includes('Loading') && this.pollCount > 2) || resp.running === 0;
            if (isDone) {
              this.statusMessage = 'Done! Building preview...';
              this.stopPolling();
              this.fetchResult();
            } else {
              this.statusMessage = status;
            }
          },
          err => {
            // Error = record gone = done
            this.stopPolling();
            this.fetchResult();
          }
        );
    }, 2000);
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private fetchResult(): void {
    this.advanceProgress(2);

    this.scorecardAgentService.getScorecardResult(this.sessionId)
      .subscribe(
        resp => {
          if (resp.success) {
            this.advanceProgress(3);
            setTimeout(() => this.showReview(resp), 300);
          } else if (resp.error === 'wrong_course') {
            // Show wrong course confirmation
            this.detectedCourseName = (resp as any).detected_name || 'unknown';
            this.expectedCourseName = (resp as any).expected_name || 'unknown';
            this.stopElapsedTimer();
            this.step = 'wrong-course';
          } else {
            this.showError(resp.error || 'Analysis failed. Please try again.');
          }
        },
        err => {
          this.showError('Could not retrieve results. Please try again.');
        }
      );
  }

  private advanceProgress(completedIndex: number): void {
    for (let i = 0; i <= completedIndex; i++) {
      this.progressSteps[i].status = 'done';
    }
    if (completedIndex + 1 < this.progressSteps.length) {
      this.progressSteps[completedIndex + 1].status = 'active';
    }
  }

  private showReview(resp: UploadAgentResponse): void {
    this.stopElapsedTimer();
    this.sessionId = resp.session_id || '';
    this.teeChanges = resp.tee_changes || [];
    this.warnings = resp.warnings || [];
    this.skippedTees = resp.skipped_tees || [];
    this.skippedTeesDetails = resp.skipped_tees_details || [];

    // Check for missing slope/rating
    this.missingRatingTees = this.teeChanges
      .filter(c => {
        const after = c.after;
        return !after.rating || after.rating === '0' || after.rating === '0.0'
            || !after.slope || after.slope === 0;
      })
      .map(c => c.tee_name);

    if (this.missingRatingTees.length > 0) {
      this.step = 'missing-rating';
    } else {
      this.step = 'review';
    }
  }

  private showError(message: string): void {
    this.stopElapsedTimer();
    this.stopPolling();
    this.errorMessage = message;
    // Mark current active step as error
    const activeStep = this.progressSteps.find(s => s.status === 'active');
    if (activeStep) activeStep.status = 'error';
  }

  // ─── Missing Rating Step ──────────────────────────────────────────────────

  onContinueWithoutRating(): void {
    this.step = 'review';
  }

  onContinueWrongCourse(): void {
    // Re-upload with skip_course_check = true
    this.step = 'processing';
    this.errorMessage = '';
    this.progressSteps = [
      { label: 'Preparing image...', status: 'done' },
      { label: 'Analyzing scorecard (skipping name check)...', status: 'active' },
      { label: 'Validating extracted data...', status: 'pending' },
      { label: 'Building preview...', status: 'pending' }
    ];
    this.startElapsedTimer();

    this.scorecardAgentService.uploadScorecard(this.courseId, this.imageBase64, true)
      .subscribe(
        resp => {
          if (resp.success && resp.session_id) {
            this.sessionId = resp.session_id;
            this.advanceProgress(1);
            this.startPolling();
          } else {
            this.showError(resp.error || 'Upload failed.');
          }
        },
        err => {
          this.showError('Could not reach the server.');
        }
      );
  }

  onUploadRatingCard(): void {
    // Go back to file picker to add a supplemental image
    // For now, just go to review — future enhancement can handle multi-image
    this.step = 'review';
  }

  // ─── Review Step ──────────────────────────────────────────────────────────

  getChangeLabel(change: TeeChange): string {
    return change.type === 'insert' ? 'New' : 'Update';
  }

  skipTee(index: number): void {
    (this.teeChanges[index] as any).skipped = true;
  }

  unskipTee(index: number): void {
    (this.teeChanges[index] as any).skipped = false;
  }

  getSqlStatements(): string[] {
    return this.teeChanges.filter(c => !(c as any).skipped).map(c => c.sql);
  }

  copyHdcpParToSkipped(): void {
    if (this.teeChanges.length === 0 || this.skippedTeesDetails.length === 0) return;

    const source = this.teeChanges[0].after;
    if (!source.holes || source.holes.length === 0) return;

    // Build UPDATE SQL for each skipped tee — ONLY par and hdcp columns
    for (const skipped of this.skippedTeesDetails) {
      if (!skipped.tees_id) continue;

      const parCols = source.holes.map((h, i) => `par_${i + 1}=${h.par}`).join(', ');
      const hdcpCols = source.holes.map((h, i) => `hdcp_${i + 1}=${h.hdcp}`).join(', ');
      const parTotal = source.par;
      const sql = `UPDATE course SET ${parCols}, ${hdcpCols}, par=${parTotal} WHERE tees_id='${skipped.tees_id}'`;

      // Build before snapshot from existing DB data
      const before = {
        tees_id: skipped.tees_id,
        teename: skipped.tee_name,
        rating: skipped.rating,
        slope: skipped.slope,
        par: skipped.par,
        total_yards: skipped.total_yards,
        holes: skipped.holes || []
      };

      // Build after snapshot — only par/hdcp change, yards stay the same
      const afterHoles = (skipped.holes || []).map((h, i) => ({
        hole: h.hole,
        par: source.holes[i]?.par || 0,
        yards: h.yards,  // Keep existing yards
        hdcp: source.holes[i]?.hdcp || 0
      }));

      this.teeChanges.push({
        type: 'update',
        tee_name: skipped.tee_name + ' (par & hdcp only)',
        before: before,
        after: {
          tees_id: skipped.tees_id,
          teename: skipped.tee_name,
          rating: skipped.rating,
          slope: skipped.slope,
          par: parTotal,
          total_yards: skipped.total_yards,
          holes: afterHoles
        },
        sql: sql
      });
    }

    // Clear skipped list since they're now included
    this.skippedTees = [];
    this.skippedTeesDetails = [];
  }

  onApply(): void {
    this.applying = true;
    this.step = 'applying';
    this.errorMessage = '';

    // Only apply non-skipped tees
    const sessionSql = this.teeChanges.filter(c => c.sql && !(c as any).skipped).map(c => c.sql);

    this.scorecardAgentService.executeSql(this.sessionId, sessionSql)
      .subscribe(
        resp => {
          if (resp.success) {
            this.applyResult = true;
            this.step = 'rating';
          } else {
            this.errorMessage = resp.error || 'Execution failed. Please try again.';
            this.applying = false;
            this.step = 'review';
            this.snackbar.open(this.errorMessage, 'Dismiss', { duration: 5000 });
          }
        },
        err => {
          this.errorMessage = 'Execution failed. Please try again later.';
          this.applying = false;
          this.step = 'review';
          this.snackbar.open(this.errorMessage, 'Dismiss', { duration: 5000 });
        }
      );
  }

  onCancel(): void {
    this.stopPolling();
    this.stopElapsedTimer();
    if (this.step === 'review' && this.teeChanges.length > 0) {
      // User saw results but cancelled — ask for rating
      this.applyResult = false;
      this.step = 'rating';
    } else {
      this.dialogRef.close(false);
    }
  }

  onRetry(): void {
    this.step = 'pick-file';
    this.errorMessage = '';
    this.selectedFileName = '';
  }

  setRating(stars: number): void {
    this.userRating = stars;
  }

  submitRating(): void {
    const teesApplied = this.teeChanges.filter(c => !(c as any).skipped).length;
    const teesSkippedByUser = this.teeChanges.filter(c => (c as any).skipped).length;
    const action = this.applyResult
      ? (teesSkippedByUser > 0 ? 'applied_partial' : 'applied')
      : 'cancelled';

    this.scorecardAgentService.rateScorecardAgent(
      this.sessionId, action, this.userRating, this.userNotes, teesApplied, teesSkippedByUser
    ).subscribe(() => {}, () => {});

    this.dialogRef.close(this.applyResult);
  }

  skipRating(): void {
    // Still log the action without a rating
    const teesApplied = this.teeChanges.filter(c => !(c as any).skipped).length;
    const teesSkippedByUser = this.teeChanges.filter(c => (c as any).skipped).length;
    const action = this.applyResult
      ? (teesSkippedByUser > 0 ? 'applied_partial' : 'applied')
      : 'cancelled';

    this.scorecardAgentService.rateScorecardAgent(
      this.sessionId, action, null, '', teesApplied, teesSkippedByUser
    ).subscribe(() => {}, () => {});

    this.dialogRef.close(this.applyResult);
  }
}
